import { useState, useEffect, useRef } from 'react';
import { Plus, FileText, ChevronDown } from 'lucide-react';
import { Matter, Goal, Task, Project, Client, MatterType, db, MatterTemplate } from '../../lib/db';
import { MatterCard } from './MatterCard';
import { MatterCardMobile } from './MatterCardMobile';
import { MatterForm } from './MatterForm';
import { CreateFromMatterTemplateModal } from '../templates/CreateFromMatterTemplateModal';
import { MatterTemplateSelectModal } from '../templates/MatterTemplateSelectModal';

interface MatterListProps {
  matters: Matter[];
  goals: Goal[];
  tasks?: Task[];
  projects?: Project[];
  clients?: Client[];
  matterTypes?: MatterType[];
  userId: string;
  onUpdate: () => void;
  onMarkComplete?: (matter: Matter) => void;
  onMarkIncomplete?: (matter: Matter) => void;
  isArchiveView?: boolean;
  showArchived?: boolean;
  setShowArchived?: (value: boolean) => void;
  openMatterId?: string | null;
  onMatterOpened?: () => void;
}

export function MatterList({ matters, goals, tasks, projects, clients, matterTypes, userId, onUpdate, onMarkComplete, onMarkIncomplete, isArchiveView = false, showArchived, setShowArchived, openMatterId, onMatterOpened }: MatterListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingMatter, setEditingMatter] = useState<Matter | null>(null);
  const [draggedMatter, setDraggedMatter] = useState<Matter | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [matterTemplates, setMatterTemplates] = useState<MatterTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MatterTemplate | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMatterTemplates();
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadMatterTemplates = async () => {
    try {
      const templates = await db.matterTemplates.findByUserId(userId);
      setMatterTemplates(templates);
    } catch (error) {
      console.error('Error loading matter templates:', error);
    }
  };

  const handleTemplateSelect = async () => {
    setShowTemplateModal(true);
    setShowDropdown(false);
  };

  const handleCreate = async (data: Partial<Matter>, customFieldValues?: Record<string, any>) => {
    try {
      const maxPosition = Math.max(...matters.map(m => m.position), -1);
      const matter = await db.matters.create({
        ...data,
        user_id: userId,
        position: maxPosition + 1,
      } as Omit<Matter, 'id' | 'created_at' | 'updated_at'>);
      
      // Save custom field values if provided
      if (customFieldValues && matter) {
        for (const [fieldId, fieldData] of Object.entries(customFieldValues)) {
          const { field, value } = fieldData as { field: any, value: any };
          
          const fieldValue: any = {
            matter_id: matter.id,
            field_id: fieldId,
          };
          
          // Set the appropriate value field based on field type
          switch (field.field_type) {
            case 'text':
            case 'multiline':
            case 'email':
            case 'url':
              fieldValue.value_text = value;
              break;
            case 'number':
              fieldValue.value_number = parseFloat(value) || null;
              break;
            case 'date':
              fieldValue.value_date = value || null;
              break;
            case 'boolean':
              fieldValue.value_boolean = value;
              break;
            case 'select':
              fieldValue.value_json = value;
              break;
          }
          
          await db.matterFieldValues.create(fieldValue);
        }
      }
      
      setIsCreating(false);
      onUpdate();
    } catch (error) {
      console.error('Error creating matter:', error);
      alert('Failed to create matter');
    }
  };

  const handleUpdate = async (data: Partial<Matter>, customFieldValues?: Record<string, any>) => {
    if (!editingMatter) return;

    try {
      await db.matters.update(editingMatter.id, userId, data);
      
      // Update custom field values if provided
      if (customFieldValues) {
        // Delete existing field values
        const existingValues = await db.matterFieldValues.findByMatterId(editingMatter.id);
        for (const value of existingValues) {
          await db.matterFieldValues.delete(value.id);
        }
        
        // Create new field values
        for (const [fieldId, fieldData] of Object.entries(customFieldValues)) {
          const { field, value } = fieldData as { field: any, value: any };
          
          const fieldValue: any = {
            matter_id: editingMatter.id,
            field_id: fieldId,
          };
          
          // Set the appropriate value field based on field type
          switch (field.field_type) {
            case 'text':
            case 'multiline':
            case 'email':
            case 'url':
              fieldValue.value_text = value;
              break;
            case 'number':
              fieldValue.value_number = parseFloat(value) || null;
              break;
            case 'date':
              fieldValue.value_date = value || null;
              break;
            case 'boolean':
              fieldValue.value_boolean = value;
              break;
            case 'select':
              fieldValue.value_json = value;
              break;
          }
          
          await db.matterFieldValues.create(fieldValue);
        }
      }
      
      setEditingMatter(null);
      onUpdate();
    } catch (error) {
      console.error('Error updating matter:', error);
      alert('Failed to update matter');
    }
  };

  const handleDelete = async (matter: Matter) => {
    try {
      await db.matters.delete(matter.id, userId);
      onUpdate();
    } catch (error) {
      console.error('Error deleting matter:', error);
      alert('Failed to delete matter');
    }
  };

  const handleArchive = async (matter: Matter) => {
    try {
      if (isArchiveView) {
        // Unarchive the matter
        const success = await db.matters.unarchive(matter.id, userId);
        if (success) {
          onUpdate();
        } else {
          alert('Failed to unarchive matter');
        }
      } else {
        // Archive the matter
        const success = await db.matters.archive(matter.id, userId);
        if (success) {
          onUpdate();
        } else {
          alert('Cannot archive matter with active deliverables');
        }
      }
    } catch (error) {
      console.error('Error archiving/unarchiving matter:', error);
      alert(isArchiveView ? 'Failed to unarchive matter' : 'Failed to archive matter');
    }
  };

  const handleDragStart = (e: React.DragEvent, matter: Matter) => {
    setDraggedMatter(matter);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetMatter: Matter) => {
    e.preventDefault();
    
    if (!draggedMatter || draggedMatter.id === targetMatter.id) {
      return;
    }

    const sortedMatters = [...matters].sort((a, b) => a.position - b.position);
    const draggedIndex = sortedMatters.findIndex(m => m.id === draggedMatter.id);
    const targetIndex = sortedMatters.findIndex(m => m.id === targetMatter.id);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const reorderedMatters = [...sortedMatters];
    const [removed] = reorderedMatters.splice(draggedIndex, 1);
    reorderedMatters.splice(targetIndex, 0, removed);

    try {
      await Promise.all(
        reorderedMatters.map((matter, index) =>
          db.matters.update(matter.id, userId, { position: index })
        )
      );
      onUpdate();
    } catch (error) {
      console.error('Error reordering matters:', error);
      alert('Failed to reorder matters');
    }

    setDraggedMatter(null);
  };

  const sortedMatters = [...matters].sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-4">
      <div ref={dropdownRef}>
        {/* Mobile Layout with Toggle */}
        <div className="sm:hidden">
        {showArchived !== undefined && setShowArchived && (
          <div className="flex items-center justify-between mb-4">
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-3 py-1.5 text-xs font-medium rounded-l-lg border ${
                  !showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(false)}
              >
                Active
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-xs font-medium rounded-r-lg border-l-0 border ${
                  showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(true)}
              >
                Archived
              </button>
            </div>
            
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-1" />
                New
                <ChevronDown className="h-4 w-4 ml-1" />
              </button>
              
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                  <div className="py-1" role="menu">
                    <button
                      onClick={() => {
                        setIsCreating(true);
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                      role="menuitem"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Blank Matter
                    </button>
                    {matterTemplates.length > 0 && (
                      <button
                        onClick={handleTemplateSelect}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                        role="menuitem"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        New From Template
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
        {/* Desktop Layout - Just the button */}
        <div className="hidden sm:flex justify-end mb-4">
          <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Matter
            <ChevronDown className="h-4 w-4 ml-2" />
          </button>
          
          {showDropdown && (
            <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1" role="menu">
                <button
                  onClick={() => {
                    setIsCreating(true);
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                  role="menuitem"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Blank Matter
                </button>
                {matterTemplates.length > 0 && (
                  <button
                    onClick={handleTemplateSelect}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                    role="menuitem"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    New From Template
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      </div>

      {isCreating && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Matter</h3>
          <MatterForm
            projects={projects}
            clients={clients}
            matterTypes={matterTypes}
            onSubmit={handleCreate}
            onCancel={() => setIsCreating(false)}
          />
        </div>
      )}

      {editingMatter && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Matter</h3>
          <MatterForm
            matter={editingMatter}
            projects={projects}
            clients={clients}
            matterTypes={matterTypes}
            onSubmit={handleUpdate}
            onCancel={() => setEditingMatter(null)}
          />
        </div>
      )}

      {sortedMatters.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-500">No matters yet. Create your first matter to organize your deliverables.</p>
        </div>
      ) : (
        <>
          {/* Desktop view */}
          <div className="hidden sm:block space-y-4">
            {sortedMatters.map(matter => (
              <MatterCard
                key={matter.id}
                matter={matter}
                goals={goals}
                tasks={tasks}
                projects={projects}
                clients={clients}
                userId={userId}
                onEdit={setEditingMatter}
                onDelete={handleDelete}
                onArchive={handleArchive}
                onMarkComplete={onMarkComplete}
                onMarkIncomplete={onMarkIncomplete}
                onDragStart={handleDragStart}
                isArchiveView={isArchiveView}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                shouldOpen={openMatterId === matter.id}
                onOpened={onMatterOpened}
              />
            ))}
          </div>
          {/* Mobile view */}
          <div className="sm:hidden space-y-3">
            {sortedMatters.map(matter => (
              <MatterCardMobile
                key={matter.id}
                matter={matter}
                goals={goals}
                tasks={tasks}
                projects={projects}
                clients={clients}
                onEdit={setEditingMatter}
                onDelete={handleDelete}
                onMarkComplete={onMarkComplete}
                onMarkIncomplete={onMarkIncomplete}
              />
            ))}
          </div>
        </>
      )}
      
      {/* Template Selection Modal */}
      <MatterTemplateSelectModal
        isOpen={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        templates={matterTemplates}
        onSelect={(template) => {
          setSelectedTemplate(template);
          setShowTemplateModal(false);
        }}
      />
      
      {/* Create from Template Modal */}
      <CreateFromMatterTemplateModal
        isOpen={!!selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        template={selectedTemplate}
        userId={userId}
        onSuccess={() => {
          setSelectedTemplate(null);
          onUpdate();
        }}
      />
    </div>
  );
}